import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BrandLogo } from '@/components/BrandLogo';
import { AppFooter } from '@/components/layout/AppFooter';
import {
  Brain, Shield, BarChart3, Users, Mail, Zap, CheckCircle2,
  AlertTriangle, XCircle, ArrowRight, HelpCircle, Eye, Upload,
  CreditCard, Lock, FileText, Layers
} from 'lucide-react';

export default function GuidePage() {
  const navigate = useNavigate();

  return (
    <div data-testid="guide-page" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <BrandLogo width={180} />
          </div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Knowledge Base</h1>
          <p className="text-sm text-muted-foreground">Everything you need to know about VendorSuite</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/support')} data-testid="guide-support-button">
          <HelpCircle className="mr-2 h-4 w-4" /> Support
        </Button>
      </div>

      <Tabs defaultValue="setup" className="space-y-6">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="setup"><CheckCircle2 className="mr-2 h-4 w-4" /> Setup Guide</TabsTrigger>
          <TabsTrigger value="ai"><Brain className="mr-2 h-4 w-4" /> How AI Works</TabsTrigger>
          <TabsTrigger value="rules"><Shield className="mr-2 h-4 w-4" /> Rules</TabsTrigger>
          <TabsTrigger value="requirements"><Layers className="mr-2 h-4 w-4" /> Requirements</TabsTrigger>
          <TabsTrigger value="scores"><BarChart3 className="mr-2 h-4 w-4" /> Scores</TabsTrigger>
          <TabsTrigger value="vendor"><Users className="mr-2 h-4 w-4" /> Vendor Portal</TabsTrigger>
          <TabsTrigger value="import"><Upload className="mr-2 h-4 w-4" /> Bulk Import</TabsTrigger>
          <TabsTrigger value="chase"><Mail className="mr-2 h-4 w-4" /> Auto-Chase</TabsTrigger>
        </TabsList>

        {/* Setup Guide - NEW */}
        <TabsContent value="setup">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <CheckCircle2 className="inline mr-2 h-5 w-5 text-primary" />
                Complete Setup Guide
              </CardTitle>
              <CardDescription>Follow these steps to get your VendorSuite account fully operational</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-8">
                {/* Step 1 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">1</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Add Your Vendors</h3>
                      <p className="text-sm text-muted-foreground mb-3">Start by adding all vendors who work on your properties.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Option A: Bulk Import (Recommended)</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Bulk Import</strong> in the sidebar</li>
                          <li>Select "Vendors" as import type</li>
                          <li>Download the CSV template</li>
                          <li>Fill in vendor data (name, type, email, phone, contact person)</li>
                          <li>Upload the CSV file</li>
                          <li>Map columns and validate data</li>
                          <li>Import all vendors at once</li>
                        </ul>
                        <p className="mt-3"><strong>Option B: Manual Entry</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Vendors</strong> page</li>
                          <li>Click "Add Vendor" button</li>
                          <li>Fill in vendor details one by one</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">2</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Create Your Properties</h3>
                      <p className="text-sm text-muted-foreground mb-3">Add all properties in your portfolio.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Bulk Import:</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Bulk Import</strong> → Select "Properties"</li>
                          <li>Upload CSV with property data</li>
                        </ul>
                        <p className="mt-2"><strong>Manual Entry:</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Properties</strong> page</li>
                          <li>Click "Add Property"</li>
                          <li>Enter name, address, type, units</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">3</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Assign Vendors to Properties</h3>
                      <p className="text-sm text-muted-foreground mb-3">Link vendors to the properties they work on.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Bulk Import:</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Bulk Import</strong> → Select "Vendor-Property Assignments"</li>
                          <li>Upload CSV mapping vendors to properties</li>
                        </ul>
                        <p className="mt-2"><strong>Manual Assignment:</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to Vendor Detail page → Properties tab → "Assign to Property"</li>
                          <li>OR go to Property Detail page → "Add Vendor"</li>
                        </ul>
                        <p className="mt-2 text-amber-600"><strong>⚠️ Important:</strong> Vendors must be assigned to properties to upload documents and be billed.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">4</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Configure Compliance Rules</h3>
                      <p className="text-sm text-muted-foreground mb-3">Set up automated compliance checking rules.</p>
                      <div className="space-y-2 text-sm">
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Compliance → Rules</strong> in the sidebar</li>
                          <li>Create rules for document requirements (e.g., "COI is required")</li>
                          <li>Add coverage minimum rules (e.g., "General Liability ≥ $1,000,000")</li>
                          <li>Set endorsement requirements (e.g., "Additional Insured required")</li>
                          <li>Configure severity levels (High, Medium, Low)</li>
                          <li>Apply rules to specific vendor types or properties</li>
                        </ul>
                        <p className="mt-2"><em>See "Rules" tab for detailed explanation.</em></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">5</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Set Up Three-Layer Requirements</h3>
                      <p className="text-sm text-muted-foreground mb-3">Define what documents each vendor type needs.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Layer 1: Vendor Type Requirements</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Compliance → Requirements</strong></li>
                          <li>Set default requirements for each vendor type (Plumber, Electrician, HVAC, etc.)</li>
                          <li>Specify: Required documents, coverage minimums, endorsements, license requirements</li>
                        </ul>
                        <p className="mt-2"><strong>Layer 2: Property Specific Requirements (Optional)</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Define specific values required for vendor documentation based on property-specific circumstances (e.g., high-rise needs higher coverage limits)</li>
                          <li>Go to property detail → Set property-specific requirements</li>
                        </ul>
                        <p className="mt-2"><strong>Layer 3: Vendor-Specific Overrides (Optional)</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>For individual vendor exceptions (e.g., sole proprietor WC exemption)</li>
                          <li>Go to vendor detail → Set vendor-specific requirements</li>
                        </ul>
                        <p className="mt-2"><em>See "Requirements" tab for detailed explanation.</em></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 6 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">6</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Notify Vendors to Submit Documents</h3>
                      <p className="text-sm text-muted-foreground mb-3">Send initial chase emails to all vendors requesting documentation.</p>
                      <div className="space-y-2 text-sm">
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li><strong>Run Chase Scan:</strong> Go to Communications page → Click "Run Chase Scan"</li>
                          <li>All vendors with missing/expired documents receive automated chase emails with magic links</li>
                          <li>Each email includes their unique portal link</li>
                          <li>Vendor clicks link → Uploads documents → AI processes automatically</li>
                        </ul>
                        <p className="mt-2"><strong>Manual Links (Alternative):</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Send individual magic links via vendor detail → "Magic Link" button</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 7 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">7</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Configure Auto-Approval (Optional)</h3>
                      <p className="text-sm text-muted-foreground mb-3">Optionally enable auto-approval to skip manual review.</p>
                      <div className="space-y-2 text-sm">
                        <p><strong>Global Auto-Approval:</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to <strong>Settings → Auto-Approve</strong> tab</li>
                          <li>Enable auto-approval globally</li>
                          <li>Set confidence threshold (e.g., 70%)</li>
                          <li>Filter by document type or vendor type</li>
                          <li>Set "No Flags Only" to auto-approve only clean extractions</li>
                        </ul>
                        <p className="mt-2"><strong>Per-Vendor Auto-Approval:</strong></p>
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li>Go to Vendor Detail → Overview tab</li>
                          <li>Toggle "Auto-Approve" switch for that specific vendor</li>
                          <li>Only their documents will auto-approve</li>
                        </ul>
                        <p className="mt-2 text-amber-600"><strong>⚠️ Note:</strong> Auto-approval is disabled by default. Always review a few extractions manually first to verify accuracy.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 8 */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-lg shrink-0">8</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Monitor & Manage Compliance</h3>
                      <p className="text-sm text-muted-foreground mb-3">Your account is now fully set up!</p>
                      <div className="space-y-2 text-sm">
                        <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                          <li><strong>Dashboard:</strong> View portfolio compliance score, expiring documents, alerts</li>
                          <li><strong>Documents:</strong> Review AI-extracted data, approve or edit extractions</li>
                          <li><strong>Auto-Chase:</strong> System automatically sends reminders for missing/expired docs</li>
                          <li><strong>Reports:</strong> Generate compliance reports for owners/stakeholders</li>
                          <li><strong>Re-Score:</strong> Click "Re-Score" on vendor to recalculate compliance after approvals</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Separator className="my-6" />
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-sm">✅ Setup Complete!</h4>
                <p className="text-xs text-muted-foreground">
                  You've configured VendorSuite from scratch. Vendors can now upload documents, AI will extract data, you'll review and approve, and the system will automatically track compliance and send reminders.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* How the AI Works */}
        <TabsContent value="ai">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}><Brain className="inline mr-2 h-5 w-5 text-primary" />How the AI Works</CardTitle>
              <CardDescription>Our two-stage AI pipeline reads compliance documents like a human assistant</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="rounded-xl border-primary/20 bg-primary/[0.03]">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Eye className="h-5 w-5 text-primary" /></div>
                      <div>
                        <p className="font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Stage 1: Vision</p>
                        <p className="text-xs text-muted-foreground">Optical Character Recognition</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">When you upload a PDF or image, our OCR engine scans every pixel to extract raw text. It handles scanned documents, handwritten notes, and multi-page certificates, with a faster path for digital PDFs.</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl border-primary/20 bg-primary/[0.03]">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Brain className="h-5 w-5 text-primary" /></div>
                      <div>
                        <p className="font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Stage 2: Logic</p>
                        <p className="text-xs text-muted-foreground">Intelligent Field Extraction</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">The extracted text is analyzed by an AI model that understands insurance terminology. It identifies policy numbers, coverage limits, expiration dates, carrier names, and compliance status — just like a trained compliance specialist would.</p>
                  </CardContent>
                </Card>
              </div>
              <div className="bg-muted/50 rounded-xl p-5">
                <p className="text-sm font-medium mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>The Pipeline Flow</p>
                <div className="flex items-center gap-3 flex-wrap">
                  {['Upload PDF', 'Text Extraction (OCR)', 'AI Analysis', 'Pending Review', 'Human Approval', 'Compliance Updated'].map((step, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">{i+1}</div>
                      <span className="text-sm">{step}</span>
                      {i < 5 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">Documents always land in "Pending Review" after AI extraction. A human must click "Approve" before the data affects vendor compliance scores.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Three-Layer Requirements */}

        {/* Compliance Rules - NEW */}
        <TabsContent value="rules">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <Shield className="inline mr-2 h-5 w-5 text-primary" />
                Compliance Rules
              </CardTitle>
              <CardDescription>Automated compliance checking based on your configured rules</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Compliance Rules are automated checks that run when documents are approved and when you click "Re-Score" on a vendor. They validate that vendors meet your insurance and licensing requirements.
              </p>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>What Are Rules?</h3>
                  <p className="text-sm text-muted-foreground">
                    Rules are IF-THEN statements that check compliance conditions. Examples: "IF vendor type is Electrician, THEN COI is required", "IF General Liability coverage exists, THEN it must be ≥ $1,000,000", "IF vendor works at Property X, THEN Additional Insured endorsement is required".
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Rule Types</h3>
                  <div className="space-y-3">
                    <div className="border rounded-lg p-3">
                      <p className="font-medium text-sm mb-1">Document Required</p>
                      <p className="text-xs text-muted-foreground">Checks if a specific document type exists and is valid. Example: "COI is required for all vendors"</p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <p className="font-medium text-sm mb-1">Coverage Minimum</p>
                      <p className="text-xs text-muted-foreground">Checks if coverage amount meets minimum. Example: "General Liability ≥ $1,000,000"</p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <p className="font-medium text-sm mb-1">Endorsement Required</p>
                      <p className="text-xs text-muted-foreground">Checks if specific endorsement is present. Example: "Additional Insured endorsement required"</p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <p className="font-medium text-sm mb-1">Document Not Expired</p>
                      <p className="text-xs text-muted-foreground">Checks if document expiration date is in the future</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>How to Configure Rules</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-sm text-muted-foreground">
                    <li>Go to <strong className="text-foreground">Compliance → Rules</strong> in the sidebar</li>
                    <li>Click "Create Rule" button</li>
                    <li>Select rule type (Document Required, Coverage Minimum, etc.)</li>
                    <li>Set threshold value (e.g., $1,000,000 for GL minimum)</li>
                    <li>Choose severity (High, Medium, Low)</li>
                    <li>Optionally filter by vendor types or specific properties</li>
                    <li>Save the rule</li>
                  </ol>
                </div>

                <Separator />

                <div className="bg-muted/50 rounded-xl p-4">
                  <h4 className="font-semibold text-sm mb-2">Rules vs. Requirements</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <strong>Requirements</strong> define <em>what</em> documents and coverage a vendor must provide (configured in the Requirements section). 
                    <strong> Rules</strong> are automated checks that validate those requirements are met. Think of Requirements as "the spec" and Rules as "the validation tests."
                  </p>
                </div>

                <Button variant="outline" size="sm" onClick={() => window.location.href='/compliance/rules'}>
                  <Shield className="mr-2 h-3 w-3" /> Manage Rules
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Three-Layer Requirements System */}
        <TabsContent value="requirements">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}><Layers className="inline mr-2 h-5 w-5 text-primary" />Three-Layer Compliance Requirements</CardTitle>
              <CardDescription>How VendorSuite determines what each vendor needs to provide</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground leading-relaxed">VendorSuite uses a three-layer hierarchy to compute the final compliance requirements for every vendor. This gives you maximum flexibility: set broad defaults, customize per property, and handle individual exceptions.</p>

              <div className="space-y-4">
                {[
                  { num: 1, title: 'Vendor Type Requirements (Default Layer)', desc: 'Every vendor type (Plumber, Electrician, HVAC, etc.) has a default set of requirements: required documents (COI, W-9, License), minimum coverage limits, required endorsements (Additional Insured, Waiver of Subrogation), trade license requirements, and expiration window rules. All vendors of that type automatically inherit these defaults.', color: 'bg-primary', example: 'Example: All Plumbers require COI + W-9 + License + Workers Comp, with $1M General Liability.' },
                  { num: 2, title: 'Property Specific Requirements (Optional Layer)', desc: 'Each property can override the vendor type defaults. Use this for properties with higher insurance requirements, additional endorsements, or specific local regulations. Property overrides augment the base requirements — they add to or replace specific values while keeping everything else from the vendor type.', color: 'bg-[hsl(36,90%,50%)]', example: 'Example: A commercial high-rise might require $2M GL instead of $1M, and add a "Primary & Non-Contributory" endorsement.' },
                  { num: 3, title: 'Vendor-Specific Overrides (Exception Layer)', desc: 'Individual vendors can have custom requirements for special cases. This is the highest-priority layer — vendor overrides always win over both type and property rules. Common uses: workers comp exemptions for sole proprietors, higher coverage for high-risk vendors, or custom document requirements.', color: 'bg-[hsl(142,56%,36%)]', example: 'Example: A sole proprietor landscaper gets a workers comp exemption, removing that document from their portal requirements.' },
                ].map(layer => (
                  <div key={layer.num} className="flex gap-4 items-start">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-full text-white text-lg font-bold shrink-0 ${layer.color}`}>{layer.num}</div>
                    <div>
                      <p className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{layer.title}</p>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{layer.desc}</p>
                      <div className="mt-2 p-2 rounded bg-muted/50 text-xs text-muted-foreground">{layer.example}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Separator />

              <div className="bg-muted/50 rounded-xl p-5">
                <p className="text-sm font-medium mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>The Merge Formula</p>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <Badge variant="outline">Vendor Type</Badge>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <Badge variant="outline">+ Property Override</Badge>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <Badge variant="outline">+ Vendor Override</Badge>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <Badge className="bg-primary">= Final Requirements</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-3">The final merged ruleset determines: what documents the vendor portal requests, what the AI must extract, what the compliance engine checks, and what appears on your dashboard.</p>
              </div>

              <div>
                <p className="text-sm font-medium mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>What You Can Configure at Each Layer</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {['Required Documents', 'Coverage Minimums', 'Required Endorsements', 'Trade Licenses', 'Expiration Window', 'Workers Comp Exemption'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" /><span>{item}</span></div>
                  ))}
                </div>
              </div>

              <Button variant="outline" size="sm" onClick={() => window.location.href='/compliance/settings'}>
                <Layers className="mr-2 h-3 w-3" /> Configure Requirements
              </Button>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Understanding Scores */}
        <TabsContent value="scores">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}><BarChart3 className="inline mr-2 h-5 w-5 text-primary" />Understanding Compliance Scores</CardTitle>
              <CardDescription>Every vendor receives a 0–100 Compliance Score based on their documents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="rounded-xl border-[hsl(142_40%_78%)] bg-[hsl(142_56%_97%)]">
                  <CardContent className="p-5 text-center">
                    <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-[hsl(142,56%,36%)] text-white mb-3">
                      <CheckCircle2 className="h-8 w-8" />
                    </div>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>80–100</p>
                    <p className="font-semibold text-[hsl(142,56%,22%)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Ready to Work</p>
                    <Badge className="mt-2 bg-[hsl(142,56%,36%)] text-white">Green</Badge>
                    <p className="text-xs text-muted-foreground mt-2">All required documents are valid and current. Coverage meets or exceeds your property requirements.</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl border-[hsl(36_80%_76%)] bg-[hsl(36_100%_97%)]">
                  <CardContent className="p-5 text-center">
                    <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-[hsl(36,90%,50%)] text-white mb-3">
                      <AlertTriangle className="h-8 w-8" />
                    </div>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>40–79</p>
                    <p className="font-semibold text-[hsl(28,95%,26%)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pending / Expiring</p>
                    <Badge className="mt-2 bg-[hsl(36,90%,50%)] text-white">Yellow</Badge>
                    <p className="text-xs text-muted-foreground mt-2">Some documents are missing, expiring soon, or awaiting review. Follow up with the vendor to resolve gaps.</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl border-[hsl(0_70%_82%)] bg-[hsl(0_85%_97%)]">
                  <CardContent className="p-5 text-center">
                    <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-[hsl(0,72%,52%)] text-white mb-3">
                      <XCircle className="h-8 w-8" />
                    </div>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>0–39</p>
                    <p className="font-semibold text-[hsl(0,72%,34%)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>High Risk</p>
                    <Badge variant="destructive" className="mt-2">Red</Badge>
                    <p className="text-xs text-muted-foreground mt-2">Critical documents are expired or missing. Vendor should not be granted property access until resolved.</p>
                  </CardContent>
                </Card>
              </div>
              <div className="bg-muted/50 rounded-xl p-5">
                <p className="text-sm font-medium mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>How Scores Are Calculated</p>
                <p className="text-sm text-muted-foreground leading-relaxed">Scores are based on your configured compliance rules. Each rule (e.g., "COI Required", "General Liability min $1M") is checked against the vendor's approved documents. The score = (rules passed / total applicable rules) × 100. Missing documents, expired coverage, and insufficient limits all reduce the score.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendor Experience */}
        <TabsContent value="vendor">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}><Users className="inline mr-2 h-5 w-5 text-primary" />The Vendor Experience</CardTitle>
              <CardDescription>What your vendors see when they receive a VendorSuite magic link</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">When you send a vendor a magic link, they receive a branded email with a direct link to their compliance portal. <strong>No account creation, no password, no login required.</strong></p>
                <Card className="mt-3 rounded-lg border-[hsl(36_80%_76%)] bg-[hsl(36_100%_96%)]"><CardContent className="p-3"><p className="text-xs text-[hsl(28,95%,26%)]"><strong>Important:</strong> Instruct your vendors to whitelist <strong>noreply.vendorsuite.io</strong> in their email contacts or safe-sender list. All compliance reminders, chases, and document requests come from this address.</p></CardContent></Card>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-3"><Mail className="h-5 w-5" /></div>
                      <p className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1. Vendor Gets Email</p>
                      <p className="text-xs text-muted-foreground">A branded email with a secure one-click portal link. Link is valid for 30 days and can be refreshed by you.</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-3"><FileText className="h-5 w-5" /></div>
                      <p className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2. Compliance Checklist</p>
                      <p className="text-xs text-muted-foreground">Vendors see exactly which documents are required, which are submitted, and which are missing or expired. Color-coded for clarity.</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-3"><Upload className="h-5 w-5" /></div>
                      <p className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>3. Upload & Done</p>
                      <p className="text-xs text-muted-foreground">Drag-and-drop upload for PDFs and images. AI processes the document immediately. Vendors can track their progress in real-time.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Separator />

              <div>
                <p className="font-semibold text-sm mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}><CreditCard className="inline mr-1 h-4 w-4" /> Vendor-Paid Portal (Enterprise)</p>
                <Card className="rounded-xl border-primary/20 bg-primary/[0.03]">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">If you've selected the <strong>Vendor-Paid billing option</strong> (Enterprise only), vendors will see a one-time $50/year payment screen before they can upload documents. This is handled securely via Stripe checkout. Once paid, the vendor has full portal access for 12 months.</p>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">Vendor pays</p>
                        <p className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$50/year</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">Your cost</p>
                        <p className="text-sm font-semibold text-[hsl(142,56%,36%)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$0/vendor</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Bulk Import */}
        <TabsContent value="import">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}><Upload className="inline mr-2 h-5 w-5 text-primary" />Bulk Import</CardTitle>
              <CardDescription>Import vendors, properties, or vendor-property assignments from CSV files</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground leading-relaxed">The Bulk Import engine lets you onboard hundreds of vendors or properties at once using a CSV file. It handles header detection, column mapping, validation, and partial imports with detailed error reporting.</p>

              <div>
                <p className="font-semibold text-sm mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>The 5-Step Import Process</p>
                <div className="space-y-3">
                  {[
                    { num: 1, title: 'Select Import Type', desc: 'Choose what you\'re importing: Vendors, Properties, or Vendor-Property Assignments. Download the CSV template to see the expected format.' },
                    { num: 2, title: 'Upload CSV', desc: 'Drag and drop your CSV file or click to browse. The system reads the file and auto-detects column headers.' },
                    { num: 3, title: 'Map Columns', desc: 'The system auto-maps your CSV headers to VendorSuite fields (e.g., "Company Name" → name, "Email Address" → email). You can manually adjust any mapping. A preview of your data is shown.' },
                    { num: 4, title: 'Validate', desc: 'Every row is validated before import. The system checks for: missing required fields, invalid email formats, duplicate vendor names (both within the CSV and against existing data), invalid phone numbers, and orphaned relationships for assignments.' },
                    { num: 5, title: 'Import', desc: 'Valid rows are imported immediately. Invalid rows are skipped but reported. You can download a CSV error report listing every issue by row number and field.' },
                  ].map(step => (
                    <div key={step.num} className="flex gap-3 items-start">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">{step.num}</div>
                      <div>
                        <p className="text-sm font-medium">{step.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <p className="font-semibold text-sm mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>What Can You Import?</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="rounded-lg">
                    <CardContent className="p-4">
                      <p className="font-medium text-sm mb-1">Vendors</p>
                      <p className="text-xs text-muted-foreground">Name (required), vendor type, email, phone, contact person, notes</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-lg">
                    <CardContent className="p-4">
                      <p className="font-medium text-sm mb-1">Properties</p>
                      <p className="text-xs text-muted-foreground">Name + address (required), city, state, zip, property type, units</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-lg">
                    <CardContent className="p-4">
                      <p className="font-medium text-sm mb-1">Assignments</p>
                      <p className="text-xs text-muted-foreground">Vendor name + property name (matched by name to existing records)</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Separator />

              <div className="bg-muted/50 rounded-xl p-5">
                <p className="text-sm font-medium mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Validation Rules</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'Missing required fields flagged',
                    'Invalid email format detected',
                    'Duplicate names caught (CSV + DB)',
                    'Invalid phone formats flagged',
                    'Vendor type validation',
                    'Orphaned assignments blocked',
                    'Partial imports supported',
                    'Downloadable error report (CSV)',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" /><span>{item}</span></div>
                  ))}
                </div>
              </div>

              <Button variant="outline" size="sm" onClick={() => window.location.href='/bulk-import'}>
                <Upload className="mr-2 h-3 w-3" /> Go to Bulk Import
              </Button>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Auto-Chase 101 */}
        <TabsContent value="chase">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}><Mail className="inline mr-2 h-5 w-5 text-primary" />Auto-Chase 101</CardTitle>
              <CardDescription>Set it and forget it — automatic follow-ups for non-compliant vendors</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground leading-relaxed">VendorSuite's automated chase engine monitors your vendors daily and sends escalating reminders when documents are missing, expired, or invalid. You configure the rules once, and the system handles the rest.</p>

              <div className="space-y-3">
                <p className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>The 3-Strike Escalation Sequence</p>
                {[
                  { num: 1, title: 'Gentle Reminder', desc: 'First automated email sent when a document is missing or expired. Friendly tone with a portal link.', days: 'Day 0', color: 'bg-primary' },
                  { num: 2, title: 'Follow-Up', desc: 'Second reminder sent 7 days later if no action taken. Firmer tone noting the pending deadline.', days: 'Day 7', color: 'bg-[hsl(36,90%,50%)]' },
                  { num: 3, title: 'Final Notice', desc: 'Third and final automated reminder. Warns of potential service suspension. After this, the system escalates to you.', days: 'Day 14', color: 'bg-destructive' },
                ].map(step => (
                  <div key={step.num} className="flex gap-4 items-start">
                    <div className="flex flex-col items-center">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full text-white text-sm font-medium ${step.color}`}>{step.num}</div>
                      {step.num < 3 && <div className="w-px h-6 bg-border" />}
                    </div>
                    <div className="pb-2">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{step.title}</p>
                        <Badge variant="outline" className="text-xs">{step.days}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-muted/50 rounded-xl p-5">
                <p className="text-sm font-medium mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Smart Stop Conditions</p>
                <p className="text-sm text-muted-foreground mb-3">The chase engine automatically stops when:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Vendor clicks the email link',
                    'Vendor uploads a document',
                    'Vendor becomes compliant',
                    'Max chase attempts reached',
                    'You manually stop chasing',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-[hsl(142,56%,36%)] shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
                  <Zap className="mr-2 h-3 w-3" /> Configure Chase Settings
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate('/communications')}>
                  <Mail className="mr-2 h-3 w-3" /> View Chase Logs
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Safety Disclaimer */}
      <Card className="rounded-xl border-[hsl(36_80%_76%)] bg-[hsl(36_100%_96%)]">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="h-5 w-5 text-[hsl(28,95%,26%)] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[hsl(28,95%,26%)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Safety Disclaimer</p>
            <p className="text-xs text-[hsl(28,95%,36%)] leading-relaxed mt-1">AI-extracted data may contain errors. Users must verify all policy expiration dates and coverage limits before granting vendor access to property sites. VendorSuite provides automated suggestions only; the final compliance decision rests solely with the Property Manager.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
